import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getApiBaseUrl, getAuthToken } from '../../../services/api';

const API_BASE = () => `${getApiBaseUrl()}/api/admin/resources`;

const DashboardTab = () => {
  const { t } = useTranslation('resourcesManagement');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState(null);
  const [popular, setPopular] = useState([]);
  const [activeUsers, setActiveUsers] = useState([]);
  const [recentExams, setRecentExams] = useState([]);

  useEffect(() => {
    const token = getAuthToken();
    const headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;

    Promise.all([
      fetch(`${API_BASE()}/stats`, { headers }).then((r) => r.json()),
      fetch(`${API_BASE()}/popular`, { headers }).then((r) => r.json()),
      fetch(`${API_BASE()}/active-users?days=30`, { headers }).then((r) => r.json()),
    ])
      .then(([statsRes, popularRes, usersRes]) => {
        if (statsRes.success) {
          setStats(statsRes.stats);
          setRecentExams(statsRes.recentExams || []);
        }
        if (popularRes.success) setPopular(popularRes.popular || []);
        if (usersRes.success) setActiveUsers(usersRes.users || []);
      })
      .catch((err) => setError(err.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading-message">{t('dashboard.loading', 'Loading...')}</div>;
  if (error) return <div className="error-message">{error}</div>;

  return (
    <div className="tab-content">
      <div className="resources-stats-grid">
        <div className="resources-stat-card">
          <h3>{t('dashboard.totalPdfs')}</h3>
          <p className="resources-stat-value">{stats?.totalPdfs ?? 0}</p>
        </div>
        <div className="resources-stat-card">
          <h3>{t('dashboard.totalWords')}</h3>
          <p className="resources-stat-value">{stats?.totalWords ?? 0}</p>
        </div>
        <div className="resources-stat-card">
          <h3>{t('dashboard.totalDownloads')}</h3>
          <p className="resources-stat-value">{stats?.totalDownloads ?? 0}</p>
        </div>
      </div>

      <h4 className="resources-section-title">{t('dashboard.popularExams')}</h4>
      <div className="resources-table-wrap">
        <table className="resources-table">
          <thead>
            <tr>
              <th>{t('examManagement.title')}</th>
              <th>{t('examManagement.level')}</th>
              <th>{t('examManagement.year')}</th>
              <th>{t('examManagement.downloads')}</th>
            </tr>
          </thead>
          <tbody>
            {(popular.length ? popular : []).slice(0, 10).map((row) => (
              <tr key={row.id}>
                <td>{row.title}</td>
                <td>{row.level}</td>
                <td>{row.year}</td>
                <td>{row.download_count ?? 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!popular.length && <p style={{ padding: 16 }}>{t('dashboard.noExams')}</p>}
      </div>

      <h4 className="resources-section-title">{t('dashboard.activeUsers')} (30 {t('dashboard.lastDays', { count: 30 })})</h4>
      <div className="resources-table-wrap">
        <table className="resources-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Name</th>
              <th>Downloads</th>
              <th>Last download</th>
            </tr>
          </thead>
          <tbody>
            {(activeUsers || []).slice(0, 20).map((u) => (
              <tr key={u.id}>
                <td>{u.email}</td>
                <td>{(u.first_name || '') + ' ' + (u.last_name || '')}</td>
                <td>{u.download_count ?? 0}</td>
                <td>{u.last_download ? new Date(u.last_download).toLocaleDateString() : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!activeUsers.length && <p style={{ padding: 16 }}>No active users in the last 30 days.</p>}
      </div>

      <h4 className="resources-section-title">{t('dashboard.recentlyAdded')}</h4>
      <div className="resources-table-wrap">
        <table className="resources-table">
          <thead>
            <tr>
              <th>{t('examManagement.title')}</th>
              <th>{t('examManagement.level')}</th>
              <th>{t('examManagement.year')}</th>
              <th>{t('examManagement.subject')}</th>
              <th>{t('examManagement.fileTypes')}</th>
              <th>{t('examManagement.dateAdded')}</th>
            </tr>
          </thead>
          <tbody>
            {(recentExams || []).map((row) => (
              <tr key={row.id}>
                <td>{row.title}</td>
                <td>{row.level}</td>
                <td>{row.year}</td>
                <td>{row.subject}</td>
                <td>{(row.fileTypes || []).join(', ')}</td>
                <td>{row.dateAdded ? new Date(row.dateAdded).toLocaleDateString() : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!recentExams.length && <p style={{ padding: 16 }}>{t('dashboard.noExams')}</p>}
      </div>
    </div>
  );
};

export default DashboardTab;
